/**
 * Unsaved Work Store (Zustand)
 *
 * The app-wide register of work that exists only in this tab and is not yet
 * durable: a recording still being captured, an inline image still uploading,
 * Image to Text whose result has not reached the note yet. While anything is
 * registered:
 *
 *  - closing or reloading the tab gets the browser's "Leave site?" prompt
 *    (one listener for the whole app, however many entries);
 *  - the workspace switcher and the note page ask before an action that would
 *    interrupt it, naming what would be lost.
 *
 * Distinct from `recordingActivityStore`, which answers a different question
 * ("is the user present?", for the idle auto-logout): a PAUSED recording is
 * unsaved but not presence, and a live session is presence with nothing local
 * to lose. Distinct from `utils/uploadActivity`, which guards tab close for
 * resumable transfers only; those run outside any page and survive in-app
 * navigation, so they never need to stop a workspace switch.
 *
 * Born from a desktop note recording thrown away by a workspace switch mid
 * client meeting (2026-10-09, PR #3690); the audit that followed found the
 * same shape in several other flows.
 */

import { useEffect } from 'react';
import { create } from 'zustand';

/** What the user would lose, phrased to complete "This would interrupt …". */
export type UnsavedWorkLabel =
  | 'a voice recording'
  | 'an image upload'
  | 'Image to text'
  | 'a voice dictation'
  | 'a text note'
  | 'a photo upload'
  | 'a video recording'
  | 'spreadsheet changes';

/**
 * What leaving interrupts. 'page' work dies with the page it was started on
 * (the default: a note recording, an inline image, Image to Text), so leaving
 * that page asks. 'app' work lives in chrome that survives navigation (the
 * Bower panel's dictation), so only a tab close or a workspace switch
 * interrupt it: the panel stays mounted through a switch and moves to the
 * other workspace's chat and draft; the composer aborts the dictation and
 * drops a transcript that arrives after the switch.
 */
export type UnsavedWorkScope = 'page' | 'app';

interface UnsavedWorkEntry {
  readonly id: number;
  readonly label: UnsavedWorkLabel;
  readonly scope: UnsavedWorkScope;
}

interface UnsavedWorkState {
  /** One entry per registration; a label can appear more than once. */
  entries: ReadonlyArray<UnsavedWorkEntry>;
}

export const useUnsavedWorkStore = create<UnsavedWorkState>(() => ({ entries: [] }));

let nextId = 1;

function warnBeforeUnload(e: BeforeUnloadEvent): void {
  // The browser shows its own text; preventDefault + returnValue are both
  // needed for the prompt to appear across browsers.
  e.preventDefault();
  e.returnValue = '';
}

/**
 * Register a piece of unsaved work and return its release. Release is
 * idempotent, so it is safe in a `finally` and an unmount cleanup alike.
 */
export function trackUnsavedWork(
  label: UnsavedWorkLabel,
  scope: UnsavedWorkScope = 'page',
): () => void {
  const id = nextId++;
  const wasEmpty = useUnsavedWorkStore.getState().entries.length === 0;
  useUnsavedWorkStore.setState((s) => ({ entries: [...s.entries, { id, label, scope }] }));
  if (wasEmpty && typeof window !== 'undefined') {
    window.addEventListener('beforeunload', warnBeforeUnload);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    useUnsavedWorkStore.setState((s) => ({ entries: s.entries.filter((e) => e.id !== id) }));
    if (useUnsavedWorkStore.getState().entries.length === 0 && typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', warnBeforeUnload);
    }
  };
}

/**
 * Test setup only: drop every entry and detach the tab-close listener.
 *
 * @internal
 */
export function resetUnsavedWork(): void {
  useUnsavedWorkStore.setState({ entries: [] });
  if (typeof window !== 'undefined') window.removeEventListener('beforeunload', warnBeforeUnload);
}

/** Registers `label` for as long as `active` is true and the caller is mounted. */
export function useUnsavedWork(
  active: boolean,
  label: UnsavedWorkLabel,
  scope: UnsavedWorkScope = 'page',
): void {
  useEffect(() => {
    if (!active) return undefined;
    return trackUnsavedWork(label, scope);
  }, [active, label, scope]);
}

/**
 * Distinct labels currently registered, in registration order. `'page'` keeps
 * only work that leaving the current page interrupts; the default is all.
 */
export function labelsOf(
  entries: ReadonlyArray<UnsavedWorkEntry>,
  scope?: UnsavedWorkScope,
): UnsavedWorkLabel[] {
  return [...new Set(entries.filter((e) => !scope || e.scope === scope).map((e) => e.label))];
}

export function getUnsavedWorkLabels(scope?: UnsavedWorkScope): UnsavedWorkLabel[] {
  return labelsOf(useUnsavedWorkStore.getState().entries, scope);
}

/** True while leaving the current page would interrupt registered work. */
export const useHasUnsavedPageWork = (): boolean =>
  useUnsavedWorkStore((s) => s.entries.some((e) => e.scope === 'page'));

/** "a voice recording", "a voice recording and an image upload", "a, b and c". */
export function describeUnsavedWork(labels: ReadonlyArray<UnsavedWorkLabel>): string {
  if (labels.length <= 1) return labels[0] ?? 'work in progress';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}
